import {
  Box,
  CloseButton,
  Flex,
  Heading,
  Image,
  Progress,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tr,
} from '@chakra-ui/react';
import React from 'react';
import { IUploadFile } from '../../interfaces';
import { useTranslation } from 'react-i18next';

interface RecentlyUploadedProps {
  files: IUploadFile[];
  onDelete: (id: string) => void;
}

const RecentlyUploaded: React.FC<RecentlyUploadedProps> = ({
  files,
  onDelete,
}) => {
  const { t } = useTranslation();
  const iconPath = '/assets/images/icons';

  /**
   * Returns the appropriate icon path based on the file extension.
   * @param {string} filename - The name of the file.
   * @returns {string} The path to the icon image.
   */
  const getIconByFileExtension = (filename: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return `${iconPath}/pdf.svg`;
      case 'doc':
      case 'docx':
        return `${iconPath}/docx.svg`;
      case 'xls':
      case 'xlsx':
        return `${iconPath}/xls.svg`;
      default:
        return `${iconPath}/file.svg`;
    }
  };

  return (
    <Box>
      <Heading size="md" fontWeight={500} mb={6} mt="54px">
        {t('recentlyUploaded')}
      </Heading>

      {files.length === 0 ? (
        <Text color="gray.500" fontSize="18px" textAlign="center">
          {t('noRecentFilesUploaded')}
        </Text>
      ) : (
        <TableContainer>
          <Table variant="unstyled">
            <Tbody>
              {[...files].map((file) => (
                <Tr key={file.id}>
                  <Td minWidth="100px" p={1}>
                    <Flex
                      justifyContent="center"
                      alignItems="center"
                      height="100%"
                    >
                      <Image
                        src={getIconByFileExtension(file.name)}
                        boxSize="40px"
                      />
                    </Flex>
                  </Td>
                  <Td width="100%" p={1}>
                    <Text color="#C6C6C6" fontSize="20px" flex="1">
                      {file.name}
                    </Text>
                  </Td>
                  {file.isUploading ? (
                    <Td minWidth={300} colSpan={2} p={1}>
                      <Progress
                        value={file.progress}
                        colorScheme="alea"
                        borderRadius="20px"
                        h={2}
                      />
                    </Td>
                  ) : (
                    <>
                      <Td minWidth={150} p={1}>
                        <Text fontSize="20px" color="black" mr={10}>
                          {file.date ? file.date : ''}
                        </Text>
                      </Td>
                      <Td minWidth={150} p={1}>
                        <Text fontSize="20px" color="black">
                          {file.time ? file.time : ''}
                        </Text>
                      </Td>
                    </>
                  )}
                  <Td minWidth="80px" p={1}>
                    <Flex
                      justifyContent="center"
                      alignItems="center"
                      height="100%"
                    >
                      {!file.isUploading && (
                        <CloseButton
                          color="red.500"
                          size="24"
                          onClick={() => onDelete(file.id)}
                        />
                      )}
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default RecentlyUploaded;
